<template>
  <div class="participant-swap">
    <v-overlay :value="swapLoading" z-index="4">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <header class="d-flex justify-space-between">
      <v-btn rounded text color="accent" :to="swapsRoute"
        ><v-icon class="mr-2">bhi-arrow-left</v-icon>Back</v-btn
      >
      <div class="participant-swap__actions text-right">
        <v-btn
          class="mb-2 ml-2 ml-lg-0"
          outlined
          color="accent"
          rounded
          @click="addProgressToSwap"
          >Additional Completed</v-btn
        >
        <v-btn
          class="mb-2 ml-2"
          outlined
          color="accent"
          rounded
          @click="openSwapEntryDialog"
          >Add entry or change category</v-btn
        >
        <v-btn
          class="mb-2 ml-2"
          color="accent"
          rounded
          @click="openSwapDialog"
          >{{ tenantTexts.participantSwap.editSwap }}</v-btn
        >
      </div>
    </header>
    <v-container v-if="swap">
      <v-row>
        <v-col :lg="5">
          <h5 class="participant-swap__label">Swapping to</h5>
          <h3>{{ swap.goal }}</h3>
          <div class="participant-swap__box">
            <div class="d-flex align-center">
              <h5 class="participant-swap__label">Status</h5>
              <span
                :class="
                  `ml-2 participant-swap__status-badge participant-swap__status-badge--${swap.status}`
                "
                >{{ swapStatus }}</span
              >
            </div>
            <div class="d-flex align-items-center mt-4">
              <v-progress-circular
                :size="56"
                :width="6"
                :color="progressColor"
                :value="swapProgress"
                class="mr-2 participant-swap__progress"
              >
                <v-icon
                  :size="28"
                  :color="progressColor"
                  v-if="swap.status === 'DONE'"
                  >bhi-check</v-icon
                >
                <span
                  :class="
                    `participant-swap__progress-label ${progressColor}--text`
                  "
                  v-else
                >
                  {{ swapProgress }}%
                </span>
              </v-progress-circular>
              <div
                class="d-flex flex-column participant-swap__progress-data flex-1 px-2"
              >
                <span class="text-nowrap">progress overview</span>
                <span class="text-nowrap"
                  >planned <strong>{{ swap.repeatGoal }}</strong></span
                >
                <span class="text-nowrap"
                  >completed <strong>{{ swap.repeatState }}</strong></span
                >
              </div>
              <div
                class="d-flex flex-column participant-swap__progress-data flex-1 px-2"
              >
                <strong class="text-nowrap">
                  {{ swapCategory }}
                </strong>
                <span>
                  {{ swap.difficultLevel | lowerCased }} &
                  {{ swap.impactLevel | lowerCased }} impact
                </span>
              </div>
            </div>
          </div>
          <div class="participant-swap__box mt-4">
            <h5 class="participant-swap__label">Swapping from</h5>
            <p>{{ swap.description }}</p>
            <h5 class="participant-swap__label mt-1" v-if="swap.tactic">
              Tactics
            </h5>
            <p>{{ swap.tactic }}</p>
            <p class="mt-2 mb-0 bhGray2--text">
              created {{ swap.creationDate | moment('MM/DD/YYYY') }}
            </p>
          </div>

          <v-btn
            class="mt-4"
            color="bhRed"
            text
            rounded
            @click="showConfirmDeleteSwapDialog"
            >{{ tenantTexts.participantSwap.deleteSwap }}</v-btn
          >
        </v-col>
        <v-col :lg="7">
          <h5 class="participant-swap__label">History</h5>
          <div
            class="participant-swap__entry mt-4"
            v-for="entry in allEntries"
            :key="
              `entry-${
                entry.note && entry.note.id ? entry.note.id : 'no-note'
              }-${entry.media && entry.media.id ? entry.media.id : 'no-media'}`
            "
          >
            <h5 class="participant-swap__label participant-swap__entry-label">
              {{ entry.creationDate | moment('MMM D, YYYY') }}
            </h5>
            <p v-if="entry.note && entry.note.content" class="mt-1">
              {{ entry.note.content }}
            </p>
            <v-img
              v-if="entry.media"
              :src="
                entry.media.thumbnailUrl ||
                  entry.media.previewUrl ||
                  (entry.media.fileTypeId === 19
                    ? require('@/assets/placeholder_video.png')
                    : entry.media.fileTypeId === 18
                    ? entry.media.fileUrl ||
                      require('@/assets/placeholder_image.png')
                    : require('@/assets/placeholder_other.png'))
              "
              @click="showPreview(entry.media)"
              :width="64"
              class="rounded-1 mt-1"
            />
            <div
              class="d-flex flex-column flex-1 mt-1 participant-swap__entry-category"
              v-if="entry.levelChange"
            >
              <strong class="text-nowrap">
                {{
                  getSwapCategory(
                    entry.levelChange.difficultLevel,
                    entry.levelChange.impactLevel,
                  )
                }}
              </strong>
              <span>
                {{ entry.levelChange.difficultLevel | lowerCased }} &
                {{ entry.levelChange.impactLevel | lowerCased }} impact
              </span>
            </div>
            <p class="mt-2 bhGray2--text" v-if="entry.createdBy">
              {{ entry.createdBy.firstname || '' }}
              {{ entry.createdBy.lastname || '' }}
            </p>
          </div>
        </v-col>
      </v-row>
    </v-container>
    <v-container v-else>
      <v-row class="fill-height">
        <v-col>
          <div
            class="d-flex flex-column align-center justify-center text-center pa-6 ma-6"
          >
            <h4 class="mb-4">{{ tenantTexts.participantSwap.notFound }}.</h4>
            <v-btn rounded text color="accent" :to="swapsRoute"
              ><v-icon class="mr-2">bhi-arrow-left</v-icon
              >{{ tenantTexts.participantSwap.goBackToSwaps }}</v-btn
            >
          </div>
        </v-col>
      </v-row>
    </v-container>
    <confirm-dialog
      title="Are you sure you want to delete this item? 🥵"
      cancelText="No"
      submitText="Yes"
      color="accent"
      :loading-buttons="swapLoading"
      :showDialog="confirmDeleteSwapDialogAlert"
      @cancel="confirmDeleteSwapDialogCancel"
      @submit="confirmDeleteSwapDialogSubmit"
    />
    <swap-dialog
      :edited-swap="swap"
      @on-edit="prepareUpdateSwap"
      :show-dialog="showEditSwapDialog"
      :submit-loading-refresh-key="submitLoadingRefreshKey"
      :key="editSwapDialogKey"
      @close-swap-dialog="closeSwapDialog"
    />
    <swap-entry-dialog
      @on-submit="prepareAddSwapEntry"
      :show-dialog="showSwapEntryDialog"
      :submit-loading-refresh-key="submitEntryLoadingRefreshKey"
      :key="swapEntryDialogKey"
      @close-swap-entry-dialog="closeSwapEntryDialog"
      :default-difficult-level="swap ? swap.difficultLevel : 'EASY'"
      :default-impact-level="swap ? swap.impactLevel : 'MAJOR'"
      :evaluation-date="lastUpdatedCategoryDate"
    />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex'
import { swapCategoryName, swapStatusName } from '@/helpers/swapCategoryName'
import globalPreviewMediaMixin from '@/mixins/globalPreviewMedia'
import RouterMixin from '@/mixins/router'
import TenantTextsMixin from '@/mixins/tenantTexts'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import SwapDialog from '@/components/dialogs/SwapDialog.vue'
import SwapEntryDialog from '@/components/dialogs/SwapEntryDialog.vue'

// @ is an alias to /src
export default {
  name: 'ParticipantSwap',
  components: { ConfirmDialog, SwapDialog, SwapEntryDialog },
  filters: {
    lowerCased: function(data = '') {
      return data.toLowerCase()
    },
  },
  mixins: [TenantTextsMixin, RouterMixin, globalPreviewMediaMixin],
  data: () => ({
    confirmDeleteSwapDialogAlert: false,
    showEditSwapDialog: false,
    editSwapDialogKey: 99,
    submitLoadingRefreshKey: 0,
    showSwapEntryDialog: false,
    swapEntryDialogKey: 1199,
    submitEntryLoadingRefreshKey: 0,
  }),
  computed: {
    ...mapState('patientsStates', ['currentPatient', 'swapLoading', 'swap']),
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('dictionariesStates', [
      'dictionaryBhUsersRoleIds',
      'dictionaryPracticeUserRole',
    ]),
    currentParticipantId() {
      return this.$route.params?.id || this.currentPatient?.id || null
    },
    swapsRoute() {
      return {
        name: 'Participant Swaps',
        path: '/participants/:id/swaps',
        params: {
          id: this.currentParticipantId,
        },
      }
    },
    swapCategory() {
      return this.swapCategoryName(
        this.swap.difficultLevel,
        this.swap.impactLevel,
      )
    },
    swapStatus() {
      return this.swapStatusName(this.swap.status)
    },
    swapProgress() {
      if (this.swap.repeatGoal === 0) return 100
      const percentage = Math.round(
        (this.swap.repeatState / this.swap.repeatGoal) * 100,
      )
      return percentage
    },
    progressColor() {
      if (this.swapProgress <= 0) return 'bhGray2'
      if (this.swapProgress >= 100) return 'bhGreenDark'
      return 'accent'
    },
    allEntries() {
      const { levelChanges, media, notes } = this.swap
      let allEntries = [
        ...this.swap.notes.map(el => ({
          creationDate: el.creationDate,
          createdBy: el.createdBy,
          note: el,
        })),
      ]

      levelChanges.forEach(el => {
        const entryIndex = allEntries.findIndex(
          entry => entry?.note?.id === el?.noteId,
        )
        if (entryIndex >= 0) {
          allEntries[entryIndex].levelChange = el
        } else {
          allEntries.push({
            creationDate: el.creationDate,
            createdBy: el.createdBy,
            levelChange: el,
          })
        }
      })

      media.forEach(el => {
        const entryIndex = allEntries.findIndex(
          entry => entry?.note?.id === el?.linkedNoteId,
        )
        if (entryIndex >= 0) {
          allEntries[entryIndex].media = el
        } else {
          allEntries.push({
            creationDate: el.creationDate,
            createdBy: el.createdBy,
            media: el,
          })
        }
      })

      const sortedEntries = [...allEntries].sort((a, b) =>
        this.$moment(b.creationDate).diff(this.$moment(a.creationDate)),
      )

      return [...sortedEntries]
    },
    lastUpdatedCategoryDate() {
      const today = this.$moment().startOf('day')

      let dates = [today]

      if (this.swap?.creationDate) {
        dates = [...dates, this.$moment(this.swap?.creationDate)]
      }

      if (this.swap?.levelChanges) {
        dates = [
          ...dates,
          ...(this.swap.levelChanges || []).map(el => this.$moment(el.date)),
        ]
      }

      const newest = this.$moment.max(dates)

      return newest.toISOString()
    },
  },
  beforeRouteLeave: function(to, from, next) {
    this.setBreadcrumbCurrentSubView('')
    this.$store.commit('patientsStates/setSwap', null)
    this.$store.commit('patientsStates/setSwapLoading', false)
    next()
  },
  mounted() {
    this.init()
  },
  created() {},
  watch: {
    currentPatient(patient) {
      if (patient?.id) this.init()
    },
    swap(newValue) {
      this.setBreadcrumbCurrentSubView(newValue?.goal || '')
    },
    $route: {
      handler(to, from) {
        // if participant id changed
        if (to.params?.id !== undefined && from.params?.id !== to.params?.id) {
          this.getPatient(to.params.id)
        }
        // if moved to participant swaps
        if (
          to.params?.swapid !== undefined &&
          from.params?.swapid !== to.params?.swapid &&
          to?.params?.swapid
        ) {
          this.init()
        }
      },
      deep: true,
    },
  },
  methods: {
    swapCategoryName,
    swapStatusName,
    ...mapMutations('appStates', ['setBreadcrumbCurrentSubView']),
    ...mapActions('patientsStates', [
      'getPatient',
      'getSwaps',
      'getSwap',
      'updateSwap',
      'removeSwap',
      'progressSwap',
      'addSwapEntry',
    ]),
    init() {
      if (!this.isCurrentPageRootLevel) {
        const swapId = this.$route.params?.swapid

        if (this.swap?.id !== swapId) {
          this.getSwap({
            participantId: this.currentParticipantId,
            swapId,
          })
        }
      }
    },
    getSwapCategory(difficultLevel, impactLevel) {
      return this.swapCategoryName(difficultLevel, impactLevel)
    },
    showPreview(media) {
      if (media) {
        this.openGlobalPreviewMedia({
          mediaUrl: media.fileUrl,
          imageName: null,
          mlResourceUrl: null,
          fileTypeId: media.fileTypeId,
        })
      }
    },
    showConfirmDeleteSwapDialog() {
      this.confirmDeleteSwapDialogAlert = true
    },
    confirmDeleteSwapDialogCancel() {
      this.confirmDeleteSwapDialogAlert = false
    },
    async confirmDeleteSwapDialogSubmit() {
      if (!this.swap?.id) return
      await this.removeSwap({
        participantId: this.currentParticipantId,
        swapId: this.swap.id,
      })
      this.confirmDeleteSwapDialogAlert = false

      this.$router.push(this.swapsRoute)
    },
    async addProgressToSwap() {
      if (!this.swap?.id) return
      await this.progressSwap({
        participantId: this.currentParticipantId,
        swapId: this.swap.id,
        date: this.$moment().format('YYYY-MM-DD'),
      })
      if (this.swap?.status === 'NEW') {
        await this.updateSwap({
          participantId: this.currentParticipantId,
          swapId: this.swap.id,
          data: {
            ...this.swap,
            status: 'IN_PROGRESS',
          },
        })
      }
      await this.getSwap({
        participantId: this.currentParticipantId,
        swapId: this.swap.id,
      })
    },
    openSwapDialog() {
      this.showEditSwapDialog = true
      this.submitLoadingRefreshKey++
    },
    closeSwapDialog() {
      this.showEditSwapDialog = false
      this.editSwapDialogKey++
    },
    async prepareUpdateSwap(swapData) {
      const response = await this.updateSwap({
        participantId: this.currentParticipantId,
        swapId: this.swap.id,
        data: swapData,
      })
      this.submitLoadingRefreshKey++
      if (response?.status === 200) {
        this.closeSwapDialog()
        this.getSwap({
          participantId: this.currentParticipantId,
          swapId: this.swap.id,
        })
      }
    },
    openSwapEntryDialog() {
      this.showSwapEntryDialog = true
      this.submitEntryLoadingRefreshKey++
    },
    closeSwapEntryDialog() {
      this.showSwapEntryDialog = false
      this.swapEntryDialogKey++
    },
    async prepareAddSwapEntry(entryData) {
      const response = await this.addSwapEntry({
        participantId: this.currentParticipantId,
        swapId: this.swap.id,
        data: entryData,
      })
      this.submitEntryLoadingRefreshKey++
      if ([200, 201].includes(response?.status)) {
        this.closeSwapEntryDialog()
        this.getSwap({
          participantId: this.currentParticipantId,
          swapId: this.swap.id,
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.participant-swap {
  flex: 1;
  padding: 24px 10px 0;
  height: 100%;

  &__label {
    color: #333333;
    font-size: 14px;
    line-height: 24px;
    margin-bottom: 0 !important;
  }

  &__entry {
    font-size: 12px;
    line-height: 22px;
    color: #3d3d3d;

    &-label {
      color: #4e4e4e;
    }

    &-category {
      color: #6c7480;
    }
  }

  &__box {
    padding: 24px 16px;
    background-color: var(--bh-gray--6);
    border-radius: 6px;

    p {
      font-size: 12px;
      line-height: 20px;
    }
  }

  &__status-badge {
    display: inline-block;
    white-space: nowrap;
    padding: 4px;
    font-size: 12px;
    line-height: 16px;
    border-radius: 4px;
    font-weight: 500;

    &--NEW {
      color: #704863;
      background-color: #efe6ec;
    }

    &--IN_PROGRESS {
      color: #19647e;
      background-color: #ccebf5;
    }

    &--DONE {
      color: #50701f;
      background-color: #e4f2cf;
    }

    &--REJECTED {
      color: #ffffff;
      background-color: var(--bh-red--light);
    }
  }

  &__progress {
    min-width: 56px;

    &-label {
      font-size: 20px;
      font-weight: 700;
      line-height: 24px;
    }

    &-data {
      font-size: 12px;
      line-height: 20px;
      color: #8e8e93;
    }
  }
}
</style>
